import {
  combineLatest,
  concatMap,
  from,
  last,
  map,
  Observable,
  of,
  scan,
  switchMap,
} from 'rxjs'
import { OrderField, OrderSearchParams } from '../models/order-search.model'
import { Order } from '../models/order.entity'
import { Page } from '../../../models/util.model'
import { parseOrderPageKeys } from '../libs/orders-page.lib'
import { Carrier, CarriersService } from '../../carriers'
import { Channel, ChannelsService } from '../../channels'
import { CountriesService, Country } from '../../countries'
import { Payment, PaymentsService } from '../../payments'
import { PickingList, PickingListsService } from '../../picking-lists'
import { Reason, ReasonsService } from '../../reasons'
import { Tag, TagsService } from '../../tags'
import { Warehouse, WarehousesService } from '../../warehouses'
import { OrdersService } from '../orders.service'
import { User, UsersService } from '../../users'
import { CurrenciesService, Currency } from '../../currencies'
import {
  OrdersListingData,
  OrdersListingPage,
} from '../models/order-listing.model'
import { Injectable } from '@angular/core'
import { uniqBy } from 'lodash'

@Injectable({
  providedIn: 'root',
})
export class OrdersListingLoader {
  private paginationLimit = 200

  constructor(
    private ordersService: OrdersService,
    private usersService: UsersService,
    private tagsService: TagsService,
    private reasonsService: ReasonsService,
    private currenciesService: CurrenciesService,
    private countriesService: CountriesService,
    private paymentsService: PaymentsService,
    private channelsService: ChannelsService,
    private warehousesService: WarehousesService,
    private pickingListsService: PickingListsService,
    private carriersService: CarriersService,
  ) {}

  /**
   * Search orders and external data
   * @param searchParams - the search params
   * @param fields - the fields
   * @param listingData - the already loaded listing data
   * @returns the observable for search orders
   */
  searchOrders$(
    searchParams: OrderSearchParams,
    fields: OrderField[],
    listingData?: OrdersListingData,
    returnAll?: boolean,
  ): Observable<OrdersListingPage> {
    return searchParams.limit !== undefined &&
      searchParams.limit > this.paginationLimit
      ? this._paginateOrders$(searchParams, fields, listingData)
      : this._searchOrders$(searchParams, fields, listingData, returnAll)
  }

  /**
   * Load external page orders data
   * @param page - the orders page
   * @param fields - the orders fields
   * @returns the observable for load external data
   */
  private _loadExtData$(
    page: Page<Order>,
    fields: OrderField[],
    listingData?: OrdersListingData,
  ): Observable<OrdersListingData> {
    const extDataKeys = parseOrderPageKeys(page)
    const obs$: Partial<{
      carriers: Observable<Carrier[]>
      channels: Observable<Channel[]>
      countries: Observable<Country[]>
      currencies: Observable<Currency[]>
      payments: Observable<Payment[]>
      pickingLists: Observable<PickingList[]>
      reasons: Observable<Reason[]>
      tags: Observable<Tag[]>
      users: Observable<User[]>
      warehouses: Observable<Warehouse[]>
    }> = {}

    if (
      (fields.includes('warehouseId') ||
        fields.includes('assignedWarehouseId')) &&
      extDataKeys.warehouseIds
    ) {
      obs$.warehouses = this.warehousesService.store$(
        extDataKeys.warehouseIds,
        listingData?.warehouses || [],
      )
    }

    if (fields.includes('header.currency') && extDataKeys.currencies) {
      obs$.currencies = this.currenciesService.store$(
        extDataKeys.currencies,
        listingData?.currencies || [],
        'code',
      )
    }

    if (fields.includes('header.paymentType') && extDataKeys.paymentIds) {
      obs$.payments = this.paymentsService.store$(
        extDataKeys.paymentIds,
        listingData?.payments || [],
      )
    }

    if (
      extDataKeys.countryCodes &&
      (fields.includes('header.billingAddress.countryCode') ||
        fields.includes('header.shippingAddress.countryCode'))
    ) {
      obs$.countries = this.countriesService.store$(
        extDataKeys.countryCodes,
        listingData?.countries || [],
      )
    }

    if (fields.includes('carrierId') && extDataKeys.carrierIds) {
      obs$.carriers = this.carriersService.store$(
        extDataKeys.carrierIds,
        listingData?.carriers || [],
      )
    }

    if (fields.includes('header.channel') && extDataKeys.channelIds) {
      obs$.channels = this.channelsService.store$(
        extDataKeys.channelIds,
        listingData?.channels || [],
      )
    }

    if (
      fields.includes('processedBy') ||
      (fields.includes('packedBy') && extDataKeys.userIds)
    ) {
      obs$.users = this.usersService.store$(
        extDataKeys.userIds || [],
        listingData?.users || [],
      )
    }

    if (
      (fields.includes('pickingListId') ||
        fields.includes('pickingListId:ex')) &&
      extDataKeys.pickingListIds
    ) {
      obs$.pickingLists = this.pickingListsService.store$(
        extDataKeys.pickingListIds,
        listingData?.pickingLists || [],
      )
    }

    if (fields.includes('tags') && extDataKeys.tagValues) {
      obs$.tags = this.tagsService.store$(
        extDataKeys.tagValues,
        listingData?.tags || [],
        'value',
        'orders',
      )
    }

    if (fields.includes('header.reasonId') && extDataKeys.reasonValues) {
      obs$.reasons = this.reasonsService.store$(
        extDataKeys.reasonValues,
        listingData?.reasons || [],
      )
    }

    if (!Object.keys(obs$).length) {
      return of({} as OrdersListingData)
    }

    return combineLatest(obs$).pipe(
      map((results) => {
        const extData: OrdersListingData = {}
        if (results.carriers) extData.carriers = results.carriers
        if (results.channels) extData.channels = results.channels
        if (results.countries) extData.countries = results.countries
        if (results.currencies) extData.currencies = results.currencies
        if (results.payments) extData.payments = results.payments
        if (results.pickingLists) extData.pickingLists = results.pickingLists
        if (results.reasons) extData.reasons = results.reasons
        if (results.tags) extData.tags = results.tags
        if (results.users) extData.users = results.users
        if (results.warehouses) extData.warehouses = results.warehouses
        return extData
      }),
    )
  }

  /**
   * Search orders with relative external data
   * @param searchParams - the search params
   * @param fields - the orders field
   * @param listingData - the already loaded listing data
   * @returns the observable<OrdersListingPage> for search orders
   */
  private _searchOrders$(
    searchParams: OrderSearchParams,
    fields: OrderField[],
    listingData?: OrdersListingData,
    returnAll?: boolean,
  ): Observable<OrdersListingPage> {
    return this.ordersService
      .search$(searchParams, returnAll)
      .pipe(
        switchMap((page) =>
          this._loadExtData$(page, fields, listingData).pipe(
            map((extData) => ({ ...page, extData })),
          ),
        ),
      )
  }

  /**
   * Search more than 200 orders with relative external data
   * @param searchParams - the search params
   * @param fields - the orders fields
   * @param listingData - the already loaded listing data
   * @returns the observable<OrdersListingData> for search orders
   */
  private _paginateOrders$(
    searchParams: OrderSearchParams,
    fields: OrderField[],
    listingData?: OrdersListingData,
  ): Observable<OrdersListingPage> {
    const totalCount = searchParams.limit || this.paginationLimit
    const pagesCount = Math.ceil(totalCount / this.paginationLimit)

    return from(
      Array.from({ length: pagesCount }, (_, i) => ({
        ...searchParams,
        limit: this.paginationLimit,
        offset: i * this.paginationLimit,
      })),
    ).pipe(
      concatMap((pageParams) =>
        this._searchOrders$(pageParams, fields, listingData),
      ),
      scan<OrdersListingPage, OrdersListingPage>(
        (acc, current) => ({
          totalCount: uniqBy(
            [...(acc.data || []), ...(current.data || [])],
            '_id',
          ).length,
          data: uniqBy([...(acc.data || []), ...(current.data || [])], '_id'),
          extData: { ...acc.extData, ...current.extData },
        }),
        { totalCount: 0, data: [], extData: {} } as OrdersListingPage,
      ),
      last(),
    )
  }
}
